import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, ErrorBox, Panel, ScreenHeading, Signed, Spinner, Tag } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n'
import { age, amount, money0, signedAmount, signedPct, units } from '../../lib/format'
import type { QueueItem } from '../../lib/types'
import { STATUS_TONE } from '../rep/Quotes'

/** §8.3 — oldest first, age prominent. */
export function Queue() {
  const { user, model } = useAuth()
  const { t } = useT()
  const navigate = useNavigate()
  const [rows, setRows] = useState<QueueItem[] | null>(null)
  const [decided, setDecided] = useState<QueueItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api.get<QueueItem[]>('/api/escalations').then(setRows).catch((e) => setError(errorMessage(e)))
    api.get<QueueItem[]>('/api/escalations/decisions').then(setDecided).catch(() => {})
  }, [])
  const cur = model?.currency ?? 'EUR'
  return (
    <div>
      <ScreenHeading
        title={user?.role === 'head' ? t('Below-floor requests') : t('Escalations')}
        subtitle={user?.role === 'head' ? t('Requests below the floor, from every team. Oldest first.') : t('Pending requests from {team}. Oldest first.', { team: user?.salesTeam ?? '' })}
        stats={
          rows
            ? [
                { label: t('Pending'), value: rows.length, foot: rows.length ? `${t('oldest')} ${age(rows[0]!.requestedAt)}` : t('nothing waiting') },
                { label: t('To the general manager'), value: rows.filter((r) => r.level === 'second').length, tone: rows.some((r) => r.level === 'second') ? 'neg' : undefined },
                { label: t('Annualised at stake'), value: money0(rows.reduce((s, r) => s + r.annualValue, 0), cur), foot: rows.some((r) => r.annualUnits === 0) ? t('{n} without recent volume', { n: rows.filter((r) => r.annualUnits === 0).length }) : t('requested vs target, a year') },
                { label: t('Decided'), value: decided?.length ?? 0, foot: t('recent decisions') },
              ]
            : undefined
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      {!rows ? (
        <Spinner />
      ) : (
        <Panel title={t('{n} pending', { n: rows.length })}>
          <DataTable
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/queue/${r.id}`)}
            empty={t('Nothing waiting.')}
            columns={[
              { key: 'age', header: t('Waiting'), render: (r) => (
                <div>
                  <div className="text-[16px] font-semibold text-slate-50">{age(r.requestedAt)}</div>
                  <div className="mt-1">{r.level === 'second' ? <Tag tone="neg">{t('general manager')}</Tag> : <Tag tone="warn">{t('sales director')}</Tag>}</div>
                </div>
              ) },
              { key: 'what', header: t('Article · customer'), render: (r) => (
                <div>
                  <div className="font-medium text-slate-100">{r.productName}</div>
                  <div className="text-[13px] text-slate-500">{r.customerName} · {r.requestedByName}{user?.role === 'head' ? ` · ${r.salesTeam}` : ''}</div>
                </div>
              ) },
              { key: 'price', header: t('Asked for'), align: 'right', render: (r) => (
                <div>
                  <div className="text-[16px] font-semibold text-slate-50">{amount(r.requestedPrice)}</div>
                  <div className="text-[12px] text-slate-500">{t('target')} {amount(r.target)}</div>
                </div>
              ) },
              { key: 'gapT', header: t('vs target'), align: 'right', render: (r) => (
                <div>
                  <Signed value={r.gapTarget} className="font-medium">{signedAmount(r.gapTarget)}</Signed>
                  <div className="text-[12px] text-slate-500">{signedPct(r.gapTargetPct)}</div>
                </div>
              ) },
              { key: 'gapF', header: t('vs floor'), align: 'right', render: (r) => <Signed value={r.gapFloor}>{signedAmount(r.gapFloor)}</Signed> },
              { key: 'val', header: t('Annualised'), align: 'right', render: (r) => (
                r.annualUnits > 0 ? (
                  <div>
                    <Signed value={r.annualValue} className="font-medium">{money0(r.annualValue, cur)}</Signed>
                    <div className="text-[12px] text-slate-500">{t('at {n} a year', { n: units(r.annualUnits) })}</div>
                  </div>
                ) : (
                  <div>
                    <div className="font-medium text-slate-500">–</div>
                    <div className="text-[12px] text-slate-500">{t('no volume in the last 12 months')}</div>
                  </div>
                )
              ) },
              { key: 'reason', header: t('Reason'), className: 'max-w-[22rem] truncate', render: (r) => <span className="text-[13px] text-slate-400" title={r.reason}>{r.reason}</span> },
            ]}
          />
        </Panel>
      )}

      {decided && decided.length > 0 && (
        <div className="mt-4">
          <Panel title={t('Recent decisions')}>
            <DataTable
              rows={decided.slice(0, 30)}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/queue/${r.id}`)}
              columns={[
                { key: 'st', header: t('Decision'), render: (r) => <Tag tone={STATUS_TONE[r.status] ?? 'neutral'}>{t(r.status)}</Tag> },
                { key: 'what', header: t('Article · customer'), render: (r) => (
                  <div>
                    <div className="font-medium text-slate-100">{r.productName}</div>
                    <div className="text-[13px] text-slate-500">{r.customerName} · {r.requestedByName}</div>
                  </div>
                ) },
                { key: 'asked', header: t('Asked → decided'), align: 'right', render: (r) => (
                  <span>
                    <span className="text-slate-500">{amount(r.requestedPrice)}</span>
                    <span className="mx-1.5 text-slate-600">→</span>
                    <span className="font-semibold text-slate-50">{r.decidedPrice === null ? '–' : amount(r.decidedPrice)}</span>
                  </span>
                ) },
                { key: 'target', header: t('Target'), align: 'right', render: (r) => <span className="text-slate-500">{amount(r.target)}</span> },
                { key: 'by', header: t('By'), render: (r) => <span className="text-[13px] text-slate-400">{r.decidedByName ?? '–'}</span> },
                { key: 'note', header: t('Note'), className: 'max-w-[24rem] truncate', render: (r) => <span className="text-[13px] text-slate-400" title={r.decisionNote ?? ''}>{r.decisionNote}</span> },
              ]}
            />
          </Panel>
        </div>
      )}
    </div>
  )
}
